import { FEATURE_FLAG_KEYS } from '@alpha-core/alpha-utils/lib/feature-flags/feature-flag.constants';
import { isFeatureEnabled } from '@alpha-core/alpha-utils/lib/feature-flags/master-feature-flags.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom, shareReplay } from 'rxjs';
import { UserListRespone } from '../interfaces/admin-filter.interface';
import { AlphaHttpClient } from './alpha-http-client';

export interface IdsGroup {
    id: string | number;
    name?: string;
    group_name: string;
    inactive?: boolean;
    description: string;
    number_of_users?: number;
}

export interface IdsGroupListResponse {
    groups: IdsGroup[];
    totalCount: number;
}

export interface IdsUserListResponse {
    umas: UserListRespone[];
    totalCount: number;
}

export interface Options {
    pageSize: number;
    pageNumber: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

type SearchMatcher<T> = (item: T, term: string) => boolean;

@Injectable({ providedIn: 'root' })
export class IdsAdapterService {
    private httpClient = inject(HttpClient);

    private userCache$: Observable<IdsUserListResponse> | null = null;
    private groupCache$: Observable<IdsGroupListResponse> | null = null;
    private alphaHttpClient = inject(AlphaHttpClient);

    private adminDomainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_ADMIN_SERVICE_URL',
        },
    };

    get isV2(): boolean {
        return isFeatureEnabled(FEATURE_FLAG_KEYS.IDS_V2_ADAPTER);
    }

    async fetchAllUsers(
        options: Options,
        hideSpinner: boolean = false,
        isAdminService: boolean = true,
    ): Promise<{ umas: UserListRespone[]; totalCount: number }> {
        if (this.isV2) {
            const { pageSize, pageNumber, search, sortBy, sortOrder } = options;
            const response = (await lastValueFrom(
                this.fetchRequest(hideSpinner, 'user', { pageSize, pageNumber, search: search ?? '' }, isAdminService),
            )) as IdsUserListResponse;
            const sorted = this.sortItems(response.umas, sortBy, sortOrder);
            return { umas: sorted, totalCount: response.totalCount };
        }

        const cached = (await lastValueFrom(
            this.getAllUsersCached(hideSpinner, options, isAdminService),
        )) as IdsUserListResponse;
        const result = this.applyClientSideOperations(
            cached.umas || [],
            options,
            (u, term) => u.user_id?.toLowerCase().includes(term) || u.username?.toLowerCase().includes(term),
        );
        return { umas: result.items, totalCount: result.totalCount };
    }

    async fetchAllGroups(
        options: Options,
        hideSpinner: boolean = false,
        isAdminService: boolean = true,
    ): Promise<{ groups: IdsGroup[]; totalCount: number }> {
        if (this.isV2) {
            const { pageSize, pageNumber, search, sortBy, sortOrder } = options;
            const response = (await lastValueFrom(
                this.fetchRequest(hideSpinner, 'group', { pageSize, pageNumber, search: search ?? '' }, isAdminService),
            )) as IdsGroupListResponse;
            const sorted = this.sortItems(response.groups, sortBy, sortOrder);
            return { groups: sorted, totalCount: response.totalCount };
        }

        const cached = (await lastValueFrom(
            this.getAllGroupsCached(hideSpinner, options, isAdminService),
        )) as IdsGroupListResponse;
        const result = this.applyClientSideOperations(
            cached.groups || [],
            options,
            (g, term) =>
                g.group_name?.toLowerCase().includes(term) ||
                g.name?.toLowerCase().includes(term) ||
                g.description?.toLowerCase().includes(term),
        );
        return { groups: result.items, totalCount: result.totalCount };
    }

    invalidateUserCache(): void {
        this.userCache$ = null;
    }

    invalidateGroupCache(): void {
        this.groupCache$ = null;
    }

    // ── Shared helpers ───────────────────────────────────────────────────

    private applyClientSideOperations<T>(
        source: T[],
        options: Options,
        searchMatcher: SearchMatcher<T>,
    ): { items: T[]; totalCount: number } {
        const { pageSize, pageNumber, search, sortBy, sortOrder } = options;

        let items = [...source];

        if (search) {
            const term = search.toLowerCase();
            items = items.filter((item) => searchMatcher(item, term));
        }

        items = this.sortItems(items, sortBy, sortOrder);

        const totalCount = items.length;
        const start = pageNumber * pageSize;
        return { items: items.slice(start, start + pageSize), totalCount };
    }

    private sortItems<T>(items: T[], sortBy?: string, sortOrder?: 'asc' | 'desc'): T[] {
        if (!sortBy) return items;
        return [...items].sort((a, b) => {
            const aVal = (a as any)[sortBy];
            const bVal = (b as any)[sortBy];
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            return 0;
        });
    }

    // ── V1 cache layer ───────────────────────────────────────────────────

    private getAllUsersCached(
        hideSpinner: boolean = false,
        options: Options,
        isAdminService: boolean = true,
    ): Observable<IdsUserListResponse> {
        if (!this.userCache$) {
            this.userCache$ = (
                this.fetchRequest(hideSpinner, 'user', options, isAdminService) as Observable<IdsUserListResponse>
            ).pipe(shareReplay(1));
        }
        return this.userCache$;
    }

    private getAllGroupsCached(
        hideSpinner: boolean = false,
        options: Options,
        isAdminService: boolean = true,
    ): Observable<IdsGroupListResponse> {
        if (!this.groupCache$) {
            this.groupCache$ = (
                this.fetchRequest(hideSpinner, 'group', options, isAdminService) as Observable<IdsGroupListResponse>
            ).pipe(shareReplay(1));
        }
        return this.groupCache$;
    }

    private fetchRequest(
        hideSpinner: boolean,
        type: 'group' | 'user',
        options: Options,
        isAdminService: boolean = true,
    ): Observable<IdsUserListResponse | IdsGroupListResponse> | any {
        const headers: Record<string, string> = {};
        if (hideSpinner) {
            headers['alpha-hide-spinner'] = hideSpinner.toString();
        }
        if (isAdminService) {
            return this.httpClient.get<IdsUserListResponse | IdsGroupListResponse>(
                `${window.environments.ALPHA_UI_ADMIN_SERVICE_URL}/${type}/fetch-all`,
                { params: { ...options }, headers },
            ) as Observable<IdsUserListResponse | IdsGroupListResponse>;
        }
        return this.alphaHttpClient.get<IdsUserListResponse | IdsGroupListResponse>(
            this.adminDomainUrl,
            `/${type}/fetch-all`,
            { params: { ...options }, headers },
        );
    }
}
